var SearchMatchLogic = Object.create(null),
    StrFormatter = require('../formatters/StrFormatter');

SearchMatchLogic.getSearchScore = getSearchScore;
SearchMatchLogic.areSimilarLabels = areSimilarLabels;
SearchMatchLogic.SEARCH_SCORE = {
  EXACT: 0,
  APPROXIMATE: 1,
  NONE: -1
};

/**
 * Évalue la correspondance entre un libellé et une recherche saisie, sans tenir compte de la casse, des accents, des espaces
 * et de la ponctuation (« TRB 15 » trouve « TRB15 »), avec une tolérance aux fautes de frappe (« GrossFit » trouve « CrossFit ») :
 * 1 faute pour un mot de 4 à 7 caractères, 2 fautes à partir de 8 caractères, aucune en dessous
 * @param {string} label - Libellé à comparer (ex : nom d'une option de liste)
 * @param {string} search - Recherche saisie
 * @returns {number} SEARCH_SCORE.EXACT si le libellé contient la recherche, SEARCH_SCORE.APPROXIMATE si le libellé correspond à la recherche aux fautes de frappe près, SEARCH_SCORE.NONE sinon
 * TU DONE 🗸
 */
function getSearchScore(label, search) {
  const labelWords = _getWords(label);
  const searchWords = _getWords(search);
  const compactLabel = labelWords.join('');

  if (!searchWords.length || compactLabel.includes(searchWords.join('')) || searchWords.every(word => compactLabel.includes(word))) {
    return SearchMatchLogic.SEARCH_SCORE.EXACT;
  }

  if (searchWords.every(word => labelWords.some(labelWord => _isApproximateWord(word, labelWord)))) {
    return SearchMatchLogic.SEARCH_SCORE.APPROXIMATE;
  }

  return SearchMatchLogic.SEARCH_SCORE.NONE;
}

/**
 * Indique si deux libellés sont identiques ou presque, sans tenir compte de la casse, des accents, des espaces et de la ponctuation
 * (« TRB 15 » et « TRB15 »), avec une tolérance aux fautes de frappe (« GrossFit Aurillac » et « CrossFit Aurillac »).
 * Les mots communs aux deux libellés sont ignorés pour que la faute porte sur ce qui les distingue (« CrossFit Albi » et « CrossFit Blois » restent différents) :
 * 1 faute pour une partie distinctive de 6 à 11 caractères, 2 fautes à partir de 12 caractères, aucune en dessous.
 * Les chiffres doivent être identiques pour que « Box 15 » et « Box 16 » restent différents
 * @param {string} labelA - Premier libellé
 * @param {string} labelB - Second libellé
 * @returns {boolean} true si les libellés sont similaires
 * TU DONE 🗸
 */
function areSimilarLabels(labelA, labelB) {
  const wordsA = _getWords(labelA);
  const wordsB = _getWords(labelB);
  const compactA = wordsA.join('');
  const compactB = wordsB.join('');

  if (!compactA || !compactB || compactA.replace(/\D/g, '') !== compactB.replace(/\D/g, '')) {
    return false;
  }

  return compactA === compactB || _areApproximateDistinctiveWords(wordsA, wordsB);
}

/* --------------- PRIVATE FUNCTION --------------- */
/**
 * Découpe une chaîne en mots en minuscules sans accents, la ponctuation servant de séparateur
 * @param {string} str - Chaîne à découper
 * @returns {Array<string>} Mots de la chaîne
 * @private
 */
function _getWords(str) {
  return StrFormatter.replaceAccents((str || '').toLowerCase()).split(/[^a-z0-9]+/).filter(word => word);
}

/**
 * Indique si un mot de la recherche correspond à un mot du libellé aux fautes de frappe près, en comparant aussi avec le début du mot du libellé pour une saisie en cours
 * @param {string} searchWord - Mot de la recherche
 * @param {string} labelWord - Mot du libellé
 * @returns {boolean} true si les mots correspondent
 * @private
 */
function _isApproximateWord(searchWord, labelWord) {
  const tolerance = searchWord.length >= 8 ? 2 : searchWord.length >= 4 ? 1 : 0;

  if (!tolerance) {
    return false;
  }

  return _getDistance(searchWord, labelWord) <= tolerance || _getDistance(searchWord, labelWord.slice(0, searchWord.length)) <= tolerance;
}

/**
 * Indique si les mots qui distinguent deux libellés (mots non communs aux deux) correspondent aux fautes de frappe près :
 * 1 faute pour une partie distinctive de 6 à 11 caractères, 2 fautes à partir de 12 caractères, aucune en dessous
 * @param {Array<string>} wordsA - Mots du premier libellé
 * @param {Array<string>} wordsB - Mots du second libellé
 * @returns {boolean} true si les parties distinctives correspondent
 * @private
 */
function _areApproximateDistinctiveWords(wordsA, wordsB) {
  const distinctiveA = wordsA.filter(word => !wordsB.includes(word)).join('');
  const distinctiveB = wordsB.filter(word => !wordsA.includes(word)).join('');
  const minLength = Math.min(distinctiveA.length, distinctiveB.length);
  const tolerance = minLength >= 12 ? 2 : minLength >= 6 ? 1 : 0;

  return tolerance > 0 && Math.abs(distinctiveA.length - distinctiveB.length) <= tolerance && _getDistance(distinctiveA, distinctiveB) <= tolerance;
}

/**
 * Distance de Levenshtein entre deux chaînes : nombre minimum de caractères à ajouter, supprimer ou remplacer pour passer de l'une à l'autre
 * @param {string} a - Première chaîne
 * @param {string} b - Seconde chaîne
 * @returns {number} Distance entre les deux chaînes
 * @private
 */
function _getDistance(a, b) {
  var previousRow = Array.from({ length: b.length + 1 }, (value, index) => index),
      currentRow;

  for (let i = 1; i <= a.length; i++) {
    currentRow = [i];

    for (let j = 1; j <= b.length; j++) {
      currentRow[j] = Math.min(previousRow[j] + 1, currentRow[j - 1] + 1, previousRow[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }

    previousRow = currentRow;
  }

  return previousRow[b.length];
}
/* --------------- /PRIVATE FUNCTION --------------- */

module.exports = SearchMatchLogic;
